import type { Position } from '../entities/RuntimeConfig';
import type { Permission } from '../vocabulary';

/**
 * Buscar, ordenar y reconocer puestos del catálogo.
 *
 * El catálogo tiene 322 puestos y llegó de dos cargas del padrón, en el orden en
 * que venían las filas: sin orden ninguno. Hasta el 13 oct 2026 el alta los
 * enseñaba así, en una lista desplegable, y dar con uno era leerla entera. Y si
 * no estaba, no había forma de agregarlo desde el portal.
 *
 * Lo que vive aquí es lo que tienen que decidir igual la pantalla y el servidor:
 * cuándo dos títulos son el mismo puesto. Sin eso, «Directora de Marketing» y
 * «directora de márketing» acaban siendo dos entradas —que es justo lo que el
 * catálogo existe para impedir; ver `Position`—. El servidor lleva su copia en
 * `functions/src/admin/positions.ts`: son dos paquetes y no comparten código.
 */

/**
 * Quién puede dar de alta un puesto: quien crea fichas o las corrige.
 *
 * Son los permisos que ya abren el alta —`createPerson`— más el de corregir una
 * ficha. No es un permiso nuevo a propósito: uno nuevo llega apagado a una base
 * ya sembrada, y esto lo necesita quien ya está capturando.
 */
export const POSITION_ADD_PERMISSIONS = [
  'alta_persona',
  'validar_cuenta',
  'editar_detalle',
  'admin',
  'areas',
] as const satisfies readonly Permission[];

/** Tres letras no son un puesto; ciento veinte ya son una descripción. */
export const POSITION_TITLE_MIN = 3;
export const POSITION_TITLE_MAX = 120;

/** Lo que alguien escribió, sin los espacios de más. */
export const cleanPositionTitle = (raw: string): string => raw.replace(/\s+/g, ' ').trim();

/**
 * La forma en que se comparan dos títulos: sin acentos, sin mayúsculas y sin
 * puntuación. «Diseñador UX/UI» y «disenador ux ui» son el mismo puesto.
 */
export const positionKey = (title: string): string =>
  title
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Por título, como se busca en una lista: la «Á» junto a la «A». */
export const sortPositions = (positions: readonly Position[]): readonly Position[] =>
  [...positions].sort((a, b) => a.title.localeCompare(b.title, 'es', { sensitivity: 'base' }));

/**
 * Los puestos que casan con lo escrito.
 *
 * Cada palabra tiene que aparecer, en el orden que sea: «marketing director»
 * encuentra «Director de Marketing». Sin nada escrito, todos.
 */
export const searchPositions = (
  positions: readonly Position[],
  query: string,
): readonly Position[] => {
  const words = positionKey(query).split(' ').filter((word) => word.length > 0);
  if (words.length === 0) return positions;
  return positions.filter((position) => {
    const key = positionKey(position.title);
    return words.every((word) => key.includes(word));
  });
};

/** El puesto que ya se llama así, aunque esté escrito distinto; `null` si no hay. */
export const findPositionByTitle = (
  positions: readonly Position[],
  title: string,
): Position | null => {
  const key = positionKey(title);
  if (key.length === 0) return null;
  return positions.find((position) => positionKey(position.title) === key) ?? null;
};

/** Si eso que se escribió puede darse de alta como puesto. */
export const isPositionTitleValid = (raw: string): boolean => {
  const title = cleanPositionTitle(raw);
  return (
    title.length >= POSITION_TITLE_MIN &&
    title.length <= POSITION_TITLE_MAX &&
    positionKey(title).length >= POSITION_TITLE_MIN
  );
};
